import React, { useState, useEffect } from 'react';
import { Button, Radio, List, Typography, Space, Dropdown, Menu, message, Popconfirm } from 'antd';
import { EllipsisOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { sprayService } from '@iso/services';
import { useSelector } from 'react-redux';
import EditTemplateModal from './EditTemplateModal';
import { TemplateModalWrapper } from './TemplateModals.styles';
import IntlMessages from '@iso/components/utility/intlMessages';

const { Text, Title } = Typography;

const TemplateDescriptionWithShowMore = ({ description, maxLength = 100 }) => {
  // Track whether the text is currently expanded or collapsed
  const [isExpanded, setIsExpanded] = useState(false);

  // Early return if no description is provided
  if (!description) return null;

  // Calculate display text based on expansion state
  const shouldTruncate = description.length > maxLength;
  const displayText =
    shouldTruncate && !isExpanded
      ? description.substring(0, maxLength) + "..."
      : description;

  return (
    <div>
      {/* Display the text content with secondary styling */}
      <Text type="secondary">{displayText}</Text>

      {/* Show toggle button only if text needs truncation */}
      {shouldTruncate && (
        <Button
          type="link"
          size="small"
          style={{ padding: 0, marginLeft: 8, height: "auto" }}
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
        >
          {isExpanded ? (
            <IntlMessages id="common.showLess" />
          ) : (
            <IntlMessages id="common.showMore" />
          )}
        </Button>
      )}
    </div>
  );
};

const UseExistingTemplateModal = ({ visible, onCancel, onUseTemplate, onCustomizeTemplate }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const propertyId = useSelector(state => state.property.activePropertyId);

  const fetchTemplates = async () => {
    if (!propertyId) return;
    
    try {
      setLoading(true);
      const response = await sprayService.getTemplates(propertyId);
      
      if (response && response.code === '0000') {
        setTemplates(response.data || []);
      } else {
        message.error('Failed to load templates');
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      message.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchTemplates();
    }
  }, [visible, propertyId]);

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setEditModalVisible(true);
  };

  const handleDelete = async (templateId) => {
    try {
      const response = await sprayService.deleteTemplate(templateId);
      
      if (response && response.code === '0000') {
        message.success('Template deleted successfully');
        fetchTemplates(); // Refresh the list
        if (selectedTemplate && selectedTemplate.id === templateId) {
          setSelectedTemplate(null);
        }
      } else {
        message.error('Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      message.error('Failed to delete template');
    }
  };

  const handleUseTemplate = () => {
    if (selectedTemplate) {
      onUseTemplate(selectedTemplate);
      setSelectedTemplate(null);
    }
  };

  const handleCustomizeTemplate = () => {
    if (selectedTemplate) {
      onCustomizeTemplate(selectedTemplate);
      setSelectedTemplate(null);
    }
  };

  const handleCancel = () => {
    setSelectedTemplate(null);
    onCancel();
  };

  const handleEditSave = () => {
    setEditModalVisible(false);
    setEditingTemplate(null);
    fetchTemplates(); // Refresh the list
  };

  const getDropdownMenu = (template) => (
    <Menu>
      <Menu.Item key="edit" icon={<EditOutlined />} onClick={() => handleEdit(template)}>
        Edit
      </Menu.Item>
      <Menu.Item key="delete" icon={<DeleteOutlined />} danger>
        <Popconfirm
          title="Are you sure you want to delete this template?"
          onConfirm={() => handleDelete(template.id)}
          okText="Yes"
          cancelText="No"
        >
          Delete
        </Popconfirm>
      </Menu.Item>
    </Menu>
  );

  return (
    <>
      <TemplateModalWrapper
        title="Use Existing Template"
        open={visible}
        onCancel={handleCancel}
        width={600}
        footer={[
          <Button key="cancel" onClick={handleCancel} className="cancel-button">
            Cancel
          </Button>,
          <Button 
            key="customize" 
            disabled={!selectedTemplate} 
            onClick={handleCustomizeTemplate}
            className="customize-btn"
          >
            Customize Template
          </Button>,
          <Button 
            key="use" 
            type="primary" 
            disabled={!selectedTemplate} 
            onClick={handleUseTemplate}
            className="btn-success"
          >
            Use Template
          </Button>
        ]}
        destroyOnClose
      >
        <div style={{ minHeight: '300px' }}>
          {templates.length === 0 && !loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <Text type="secondary">No templates found</Text>
            </div>
          ) : (
            <Radio.Group 
              value={selectedTemplate?.id} 
              onChange={(e) => setSelectedTemplate(templates.find(t => t.id === e.target.value))}
              style={{ width: '100%' }}
            >
              <List
                loading={loading}
                dataSource={templates}
                renderItem={(template) => (
                  <List.Item
                    key={template.id}
                    style={{ 
                      padding: '16px',
                      border: selectedTemplate?.id === template.id ? '2px solid #1890ff' : '1px solid #f0f0f0',
                      borderRadius: '6px',
                      marginBottom: '8px',
                      cursor: 'pointer'
                    }}
                    onClick={() => setSelectedTemplate(template)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Radio value={template.id} style={{ marginRight: '16px' }} />
                      <div style={{ flex: 1 }}>
                        <Title level={5} style={{ margin: 0, marginBottom: '4px' }}>
                          {template.template_name || template.job_name}
                        </Title>
                        <TemplateDescriptionWithShowMore
                          description={template.template_description}
                          maxLength={100}
                        />
                      </div>
                      <Dropdown 
                        overlay={getDropdownMenu(template)} 
                        trigger={['click']}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button 
                          type="text" 
                          icon={<EllipsisOutlined />}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Dropdown>
                    </div>
                  </List.Item>
                )}
              />
            </Radio.Group>
          )}
        </div>
      </TemplateModalWrapper>

      <EditTemplateModal
        visible={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onSave={handleEditSave}
        template={editingTemplate}
      />
    </>
  );
};

export default UseExistingTemplateModal;